import { useLocation } from 'react-router';
import { useMemo } from 'react';
import { generatedTypesNew } from '@css-xp/graphtypes';
import { BASE_PATH_WITH_TXA, routeFragments, routePaths } from '@css-xp/ppc-constants';
import { PartnerSearchContext } from '@css-xp/ppc-types';

type PathRulesConfig = {
  hasOrderPath?: boolean;
  hasQuotesPath?: boolean;
};

export const getRouteUrl = (routePath: string, data: { [key: string]: string }) => {
  return Object.keys(data).reduce((acc, key) => acc.replace(`:${key}`, data[key]), routePath);
};
/**
 * Construct a URL having company name encoded with Base64 encoding.
 * @param companyName
 * @param txaId
 */
export const getCompanyNameRoute = ({ companyName, txaId }: { companyName: string; txaId?: string }) => {
  let name;
  try {
    name = encodeURIComponent(companyName);
  } catch (e) {
    name = companyName;
  }
  const params = {
    companyName: name,
    ...(txaId ? { txaId } : {}),
  };

  return getRouteUrl(routePaths.customerAddressDetailsByCompany, params);
};

/**
 * Get the decoded value of the company name from the URL which has base64 encoding
 * @param encodedName
 */
export const getCompanyNameFromRoute = (encodedName?: string) => {
  let name;
  if (!encodedName) {
    return encodedName;
  }
  try {
    name = decodeURIComponent(encodedName);
  } catch (e) {
    name = encodedName;
  }
  return name;
};

const PathGenerationRules = [
  {
    searchContextsToMatch: [PartnerSearchContext.SUBSCRIPTION_ID, PartnerSearchContext.ALL],
    graphEntitiesToMatch: [generatedTypesNew.GraphEntity.CCP_ENTITLEMENT],
    path: routeFragments.SUBSCRIPTIONS,
  },
  {
    searchContextsToMatch: [PartnerSearchContext.INVOICE_ID, PartnerSearchContext.ALL],
    graphEntitiesToMatch: [generatedTypesNew.GraphEntity.INVOICE],
    path: routeFragments.INVOICES,
  },
  {
    searchContextsToMatch: [PartnerSearchContext.REFUND_ID, PartnerSearchContext.ALL],
    graphEntitiesToMatch: [generatedTypesNew.GraphEntity.REFUND, generatedTypesNew.GraphEntity.CREDITNOTE],
    path: routeFragments.REFUNDS,
  },
  {
    searchContextsToMatch: [PartnerSearchContext.SITE_URL, PartnerSearchContext.ALL],
    graphEntitiesToMatch: [generatedTypesNew.GraphEntity.CLOUD_SITE],
    path: routeFragments.CLOUD_SITE_PATH,
  },
  {
    searchContextsToMatch: [PartnerSearchContext.ORGANISATION_ID, PartnerSearchContext.ALL],
    graphEntitiesToMatch: [generatedTypesNew.GraphEntity.ORGANIZATION],
    path: routeFragments.ORGANISATIONS,
  },
  {
    searchContextsToMatch: [PartnerSearchContext.INVOICE_GROUP, PartnerSearchContext.ALL],
    graphEntitiesToMatch: [generatedTypesNew.GraphEntity.INVOICE_GROUP],
    path: routeFragments.BILLING_PROFILES,
  },
];
const orderPathGenerationRule = {
  searchContextsToMatch: [PartnerSearchContext.ORDER, PartnerSearchContext.ALL],
  graphEntitiesToMatch: [generatedTypesNew.GraphEntity.ORDER],
  path: routeFragments.ORDER_DETAILS_PATH,
};

const quotePathGenerationRule = {
  searchContextsToMatch: [PartnerSearchContext.QUOTE, PartnerSearchContext.ALL],
  graphEntitiesToMatch: [generatedTypesNew.GraphEntity.QUOTE],
  path: routeFragments.QUOTES,
};

const generatePath = (
  searchContext: string,
  directMatch: generatedTypesNew.DirectMatch,
  pathRulesConfig?: PathRulesConfig
) => {
  const orderRule = pathRulesConfig?.hasOrderPath ? [orderPathGenerationRule] : [];
  const quoteRule = pathRulesConfig?.hasQuotesPath ? [quotePathGenerationRule] : [];
  const updatedPathGenerationRules = [...PathGenerationRules, ...orderRule, ...quoteRule];
  const allowedPathGenerationRule = updatedPathGenerationRules.find(
    (pathGenerationRule) =>
      pathGenerationRule.searchContextsToMatch.includes(searchContext as PartnerSearchContext) &&
      pathGenerationRule.graphEntitiesToMatch.includes(directMatch.entity as generatedTypesNew.GraphEntity)
  );

  return allowedPathGenerationRule?.path ?? routeFragments.SEARCH;
};

export const getRouteFromDirectMatch = (
  directMatch: generatedTypesNew.DirectMatch,
  txaId: string,
  searchContext: string,
  pathRulesConfig?: PathRulesConfig
): { pathToRedirectTo: string; isValidDirectMatch: boolean } => {
  const path = generatePath(searchContext, directMatch, pathRulesConfig);
  // directMatch.identifier will never be undefined or null in case a direct match was found
  const encodedIdentifier = encodeURIComponent(directMatch.identifier as string);
  return {
    pathToRedirectTo: `${getRouteUrl(BASE_PATH_WITH_TXA, { txaId })}/${path}/${encodedIdentifier}`,
    isValidDirectMatch: path !== routeFragments.SEARCH,
  };
};

export function useQueryParams() {
  const { search } = useLocation();

  return useMemo(() => new URLSearchParams(search), [search]);
}
